import { LegacyRef, useEffect, useState } from "react";

import { css } from "@compiled/react";
import { FormattedMessage, useIntl } from "react-intl";
import { CCPOfferingContent } from "src/contentful";

import ModalDialog, { ModalTransition } from "@atlaskit/modal-dialog";

import Button from "@/components/wpl/button/src";
import { DEFAULT_VALUES, useLocalizedHref } from "@/libraries/web-framework";

import {
    COUNTRY_CODE_TO_CURRENCY,
    LOCALE_CACHE_KEY,
    UserLocale
} from "../../../../../../utils/useGeoCurrency";
import {
    CurrencyISOCode,
    DEFAULT_CURRENCY_ISO_CODE,
    DefaultPlanLimits,
    OfferingHamsMap,
    OfferingSKUMap,
    PricingPlans,
    ProductKey,
    ProductPlan,
    SpecialPlanNames
} from "../constants";
import { usePriceFinderStore } from "../store/store";
import {
    usePriceFinderRequests,
    usePricing
} from "../utils/use-price-finder-data";

import { PriceCardBody } from "./body";
import PriceCard from "./price-card";

const ctaButtonStyles = css({
    font: "Charlie Text",
    fontSize: "16px",
    fontWeight: "400",
    lineHeight: "20px",
    height: "auto",
    textDecoration: "none",
    width: "fit-content",
    whiteSpace: "normal",
    paddingBlockStart: "8px",
    paddingBlockEnd: "8px"
});

const cardContainerWrapperStyles = css({
    margin: "auto",
    maxWidth: "1408px"
});

const cardContainerStyles = css({
    marginBlockStart: "16px"
});

export const centerJustifiedStyling = {
    display: "flex",
    flexDirection: "column",
    flexGrow: "1",
    justifyContent: "center",
    lineHeight: "24px"
};

type PriceCardsContainerProps<P extends ProductKey> = {
    productKey: string;
    productPlans: ProductPlan<P>[];
    signupURL: string;
    contactURL: string;
    countryCode: UserLocale;
    fontSize: string;
    cardRef?: LegacyRef<HTMLDivElement>;
    freeLimitCount: number;
    enterpriseMinimumCount: number;
    hamsProductKey: string;
    hamsPricingData: OfferingHamsMap<P, PricingPlans>;
    ccpPricingData: OfferingSKUMap<P, CCPOfferingContent>;
    localeISOCode: string;
    dataProductKey: P;
};

const PriceCardsContainer = <P extends ProductKey>({
    productKey,
    productPlans,
    signupURL,
    contactURL,
    countryCode,
    fontSize,
    cardRef,
    freeLimitCount,
    enterpriseMinimumCount,
    hamsProductKey,
    hamsPricingData,
    ccpPricingData,
    localeISOCode,
    dataProductKey
}: PriceCardsContainerProps<P>) => {
    const intl = useIntl();

    const [showModal, setShowModal] = useState(false);
    const [
        {
            unitCount,
            billingCycle,
            exampleMonthlyUnitCount,
            exampleAnnualUnitCount,
            currencyISOCode,
            messages
        },
        actions
    ] = usePriceFinderStore();

    const recommendedPlanName = getRecommendedPlanName(unitCount, productPlans);

    const fetchPriceCalcs = usePriceFinderRequests(
        [exampleMonthlyUnitCount, exampleAnnualUnitCount],
        hamsProductKey,
        productPlans,
        enterpriseMinimumCount,
        unitCount,
        billingCycle
    );

    const finalPriceCalcs = usePricing(
        ccpPricingData,
        hamsPricingData,
        fetchPriceCalcs,
        currencyISOCode,
        localeISOCode
    );

    useEffect(() => {
        actions.updatePriceCalcs(finalPriceCalcs);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [unitCount, billingCycle, currencyISOCode]);

    useEffect(() => {
        const countryCodeFromSessionStorage = (sessionStorage.getItem(
            LOCALE_CACHE_KEY
        ) || countryCode) as UserLocale;

        if (countryCodeFromSessionStorage) {
            sessionStorage.setItem(
                LOCALE_CACHE_KEY,
                countryCodeFromSessionStorage
            );

            const updatedCurrencyISOCode = (COUNTRY_CODE_TO_CURRENCY[
                countryCodeFromSessionStorage
            ] || DEFAULT_CURRENCY_ISO_CODE) as CurrencyISOCode;

            actions.updateCurrencyISOCode(updatedCurrencyISOCode);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const localizedSignupURL = useLocalizedHref(signupURL);
    const localizedContactURL = useLocalizedHref(contactURL);

    return (
        <div css={cardContainerWrapperStyles}>
            <div
                css={[
                    cardContainerStyles,
                    css({
                        "@media (min-width: 1024px)": {
                            display: "grid",
                            maxWidth: "1408px",
                            gridTemplateRows: "1fr",
                            marginBlockStart: "25px",
                            gridTemplateColumns: `repeat(${productPlans.length}, minmax(0, 1fr))`
                        }
                    })
                ]}
                ref={cardRef}
            >
                {productPlans.map((productPlan, idx, productPlans) => {
                    const {
                        planName,
                        signupURLSuffix,
                        planFeatureMessages,
                        description,
                        signupLimit
                    } = productPlan;

                    const isHighlighted = planName === recommendedPlanName;

                    const overSignupLimit =
                        unitCount >
                        (signupLimit || DefaultPlanLimits.UNIT_LIMIT);

                    let ctaButton = (
                        <Button
                            disabled={overSignupLimit}
                            text={intl.formatMessage({
                                id: "Pricing.PriceCard.StartTrial",
                                defaultMessage: "Start Trial"
                            })}
                            type={isHighlighted ? "primary" : "secondary"}
                            css={ctaButtonStyles}
                            url={localizedSignupURL + (signupURLSuffix || "")}
                            dataEventProps={{
                                "data-product-key": dataProductKey,
                                "data-event-component": "linkButton",
                                "data-event-container": "priceBlock",
                                "data-event": "clicked",
                                "data-label": `${planName}-start-trial`,
                                "data-label-english": `${planName}-start-trial`
                            }}
                        />
                    );

                    switch (planName) {
                        case SpecialPlanNames.FREE:
                            ctaButton = (
                                <Button
                                    text={intl.formatMessage({
                                        id: "Pricing.PriceCard.GetItNow",
                                        defaultMessage: "Get it now"
                                    })}
                                    type={
                                        isHighlighted ? "primary" : "secondary"
                                    }
                                    css={ctaButtonStyles}
                                    url={
                                        unitCount > freeLimitCount
                                            ? undefined
                                            : localizedSignupURL +
                                                  signupURLSuffix || ""
                                    }
                                    inputType={
                                        unitCount > freeLimitCount
                                            ? "button"
                                            : undefined
                                    }
                                    onClick={(e) => {
                                        if (unitCount > freeLimitCount) {
                                            e.preventDefault();
                                            setShowModal(true);
                                        }
                                    }}
                                    dataEventProps={{
                                        "data-product-key": dataProductKey,
                                        "data-event-component": "linkButton",
                                        "data-event-container": "priceBlock",
                                        "data-event": "clicked",
                                        "data-label": `${planName}-get-it-now`,
                                        "data-label-english": `${planName}-get-it-now`
                                    }}
                                />
                            );
                            break;

                        case SpecialPlanNames.ENTERPRISE:
                            ctaButton = (
                                <Button
                                    text={intl.formatMessage({
                                        id: "Pricing.PriceCard.ContactSales",
                                        defaultMessage: "Contact sales"
                                    })}
                                    type={
                                        isHighlighted ? "primary" : "secondary"
                                    }
                                    css={ctaButtonStyles}
                                    url={localizedContactURL}
                                    dataEventProps={{
                                        "data-product-key": dataProductKey,
                                        "data-event-component": "linkButton",
                                        "data-event-container": "priceBlock",
                                        "data-event": "clicked",
                                        "data-label": `${planName}-contact-sales`,
                                        "data-label-english": `${planName}-contact-sales`
                                    }}
                                />
                            );
                            break;
                    }

                    return (
                        <PriceCard
                            key={idx}
                            planName={planName}
                            description={description}
                            highlighted={isHighlighted}
                            ctaButton={ctaButton}
                            planFeatureMessages={planFeatureMessages}
                            featureMessagePlanName={
                                productPlans[idx - 1] &&
                                productPlans[idx - 1]?.planName
                            }
                            first={idx === 0}
                            last={idx === productPlans.length - 1}
                        >
                            <PriceCardBody
                                billingCycle={billingCycle}
                                fontSize={fontSize}
                                overSignupLimit={overSignupLimit}
                                planName={planName}
                                currencyISOCode={currencyISOCode}
                                unitCount={unitCount}
                                productKey={productKey}
                                enterpriseMinimumCount={enterpriseMinimumCount}
                                priceCalcIdx={idx}
                                contactURL={localizedContactURL}
                            />
                        </PriceCard>
                    );
                })}
            </div>
            <ModalTransition>
                {showModal && (
                    <ModalDialog onClose={() => setShowModal(false)}>
                        <h4
                            css={css({
                                fontSize: "1.25rem",
                                margin: 0,
                                padding: "26px 24px 12px"
                            })}
                        >
                            <FormattedMessage
                                {...messages.freeLimitModalTitle}
                                values={{ ...DEFAULT_VALUES, freeLimitCount }}
                            />
                        </h4>
                        <div
                            css={css({
                                padding: "0 24px"
                            })}
                        >
                            <FormattedMessage
                                {...messages.freeLimitModalMessage}
                                values={{ ...DEFAULT_VALUES, freeLimitCount }}
                            />
                        </div>
                        <div
                            css={css({
                                display: "flex",
                                justifyContent: "flex-end",
                                padding: "24px",
                                width: "100%",
                                gap: "8px"
                            })}
                        >
                            <Button
                                onClick={() => setShowModal(false)}
                                text={intl.formatMessage({
                                    id: "Pricing.PriceCard.Cancel",
                                    defaultMessage: "Cancel"
                                })}
                                type="secondary"
                                color="white"
                                css={css({
                                    "color": "#42526e !important",
                                    ":hover": {
                                        backgroundColor: "#091e4214 !important"
                                    }
                                })}
                            />
                            <Button
                                text={intl.formatMessage({
                                    id: "Pricing.PriceCard.SignUpForFree",
                                    defaultMessage: "Sign up for free"
                                })}
                                type="primary"
                                url={
                                    localizedSignupURL +
                                    (productPlans[0]?.signupURLSuffix || "")
                                }
                            />
                        </div>
                    </ModalDialog>
                )}
            </ModalTransition>
        </div>
    );
};

const getRecommendedPlanIdx = (unitCount: number, minUnitCounts: number[]) => {
    const len = minUnitCounts.length - 1;

    return (
        len -
        minUnitCounts
            .reverse()
            .findIndex((minUnitCount) => unitCount > minUnitCount)
    );
};

export const getRecommendedPlanName = <P extends ProductKey>(
    unitCount: number,
    productPlans: ProductPlan<P>[]
) => {
    const recommendedPlanIdx = getRecommendedPlanIdx(
        unitCount,
        productPlans.map((productPlan) => productPlan.recommendationLimit)
    );

    return productPlans[recommendedPlanIdx]?.planName ?? "";
};

export { PriceCardsContainer };
